import { useEffect, useRef } from 'react';

const PARTICLES = [
  { id: 0,  left: 5,  bottom: 12, delay: 0,   dur: 8,   size: 8  },
  { id: 1,  left: 13, bottom: 22, delay: 1.4, dur: 9,   size: 6  },
  { id: 2,  left: 24, bottom: 8,  delay: 2.7, dur: 7,   size: 10 },
  { id: 3,  left: 33, bottom: 32, delay: 0.6, dur: 10,  size: 7  },
  { id: 4,  left: 47, bottom: 6,  delay: 3.3, dur: 8.5, size: 8  },
  { id: 5,  left: 56, bottom: 18, delay: 1.9, dur: 7,   size: 6  },
  { id: 6,  left: 64, bottom: 38, delay: 4.0, dur: 9.5, size: 11 },
  { id: 7,  left: 72, bottom: 10, delay: 2.1, dur: 6.5, size: 7  },
  { id: 8,  left: 81, bottom: 26, delay: 0.4, dur: 8,   size: 9  },
  { id: 9,  left: 91, bottom: 16, delay: 3.9, dur: 7.5, size: 6  },
  { id: 10, left: 17, bottom: 44, delay: 5.2, dur: 8,   size: 8  },
  { id: 11, left: 41, bottom: 50, delay: 2.9, dur: 10,  size: 7  },
  { id: 12, left: 59, bottom: 54, delay: 0.9, dur: 7,   size: 10 },
  { id: 13, left: 77, bottom: 44, delay: 4.7, dur: 8,   size: 6  },
  { id: 14, left: 88, bottom: 58, delay: 6.1, dur: 7,   size: 9  },
  { id: 15, left: 28, bottom: 60, delay: 3.5, dur: 9,   size: 7  },
  { id: 16, left: 50, bottom: 65, delay: 7.0, dur: 8,   size: 8  },
  { id: 17, left: 96, bottom: 50, delay: 1.6, dur: 7.5, size: 6  },
  { id: 18, left: 38, bottom: 20, delay: 4.8, dur: 9,   size: 10 },
  { id: 19, left: 68, bottom: 70, delay: 2.3, dur: 8,   size: 7  },
];

export default function HeroSection() {
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    setTimeout(() => {
      el.classList.add('hero-loaded');
    }, 150);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative w-full min-h-screen flex flex-col items-center justify-start sm:justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/388f18fcf1e5836174a7aff5878b0c32.jpg"
          alt="헤라브로우클리닉 배경"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/60"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-plum-dark/30 via-transparent to-plum-dark/20"></div>
      </div>

      {/* Gold Scan Line — sweeps across */}
      <div className="hero-scan-line z-[1]"></div>

      {/* Floating Gold Particles */}
      <div className="absolute inset-0 pointer-events-none z-[2]">
        {PARTICLES.map((p) => (
          <div
            key={p.id}
            className="absolute rounded-full"
            style={{
              left: `${p.left}%`,
              bottom: `${p.bottom}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              background: `radial-gradient(circle, #FFF8DC 0%, #F0D070 40%, #C9A050 75%, transparent 100%)`,
              animation: `floatParticle ${p.dur}s ease-in-out ${p.delay}s infinite`,
              boxShadow: `0 0 ${p.size * 2.5}px ${p.size * 1.5}px rgba(240, 208, 112, 0.5), 0 0 ${p.size * 5}px rgba(201, 160, 80, 0.25)`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div
        ref={titleRef}
        className="relative z-10 w-full text-center px-5 sm:px-6 pt-28 sm:pt-0 pb-20 sm:pb-0"
      >
        {/* Badge */}
        <div className="hero-item" style={{ transitionDelay: '0.1s' }}>
          <div className="inline-flex flex-wrap items-center justify-center gap-2 bg-gold/20 border border-gold/50 text-gold-shine px-3 sm:px-4 py-2 rounded-2xl text-xs sm:text-sm font-sans mb-7 sm:mb-6 backdrop-blur-sm max-w-[calc(100vw-2.5rem)] text-center">
            <i className="ri-award-fill flex-shrink-0"></i>
            <span className="leading-snug">대한문신사중앙회 서울중구지부장 운영 · 한의학 박사 상주</span>
          </div>
        </div>

        {/* Main Title */}
        <div className="hero-item" style={{ transitionDelay: '0.3s' }}>
          <h1 className="font-serif text-[28px] sm:text-4xl md:text-6xl lg:text-7xl text-white font-bold leading-tight mb-4 sm:mb-4">
            헤라브로우크리닉
          </h1>
          <p className="font-serif text-base sm:text-xl md:text-2xl text-gold-shine italic mb-3 sm:mb-3">
            Hera Brow Clinic · Hera 漢方 Brow Clinic
          </p>
          <div className="w-24 h-px bg-gold mx-auto mb-6 sm:mb-6"></div>
        </div>

        {/* Taglines */}
        <div className="hero-item" style={{ transitionDelay: '0.55s' }}>
          <p className="font-sans text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed mb-3">
            예술적인 문신과 한의학의 정교한 만남
          </p>
          <p className="font-sans text-sm text-white/70 max-w-2xl mx-auto mb-3">
            The Artful Union of Tattoo Artistry &amp; Oriental Medicine
          </p>
          <p className="font-sans text-xs text-white/60 max-w-2xl mx-auto mb-8 sm:mb-10">
            艺术纹身与韩方医学의精妙融合 · アート文身と韓方医学の精妙なる出逢い
          </p>
        </div>

        {/* Multi-language Tagline Badges */}
        <div className="hero-item" style={{ transitionDelay: '0.75s' }}>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-10">
            {[
              { lang: 'KR', text: '반영구 문신 전문' },
              { lang: 'EN', text: 'Semi-Permanent Makeup' },
              { lang: '中', text: '半永久化妆' },
              { lang: 'JP', text: 'アートMake-up' },
            ].map((b) => (
              <span key={b.lang} className="flex items-center gap-1.5 bg-white/10 border border-white/25 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs sm:text-sm font-sans">
                <span className="text-gold-shine font-bold text-xs">{b.lang}</span>
                {b.text}
              </span>
            ))}
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="hero-item" style={{ transitionDelay: '0.95s' }}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-10 px-4 sm:px-0">
            <button
              onClick={() => scrollTo('#reservation')}
              className="btn-gold-pulse w-full sm:w-auto px-8 py-3.5 bg-gold text-white font-sans font-semibold rounded-full hover:bg-gold-dark transition-all duration-300 whitespace-nowrap cursor-pointer text-base"
            >
              <i className="ri-calendar-check-line mr-2"></i>
              온라인 예약하기
            </button>
            <a
              href="tel:01067404061"
              className="w-full sm:w-auto text-center px-8 py-3.5 bg-white/15 text-white border border-white/40 font-sans font-medium rounded-full hover:bg-white/25 transition-all duration-300 whitespace-nowrap cursor-pointer text-base backdrop-blur-sm"
            >
              <i className="ri-phone-line mr-2"></i>
              010-6740-4061
            </a>
          </div>
        </div>

        {/* Info Strip */}
        <div className="hero-item" style={{ transitionDelay: '1.15s' }}>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center items-center gap-3 sm:gap-6 text-sm text-white/80 font-sans">
            <div className="flex items-center justify-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center text-gold-shine flex-shrink-0"><i className="ri-map-pin-line"></i></span>
              <span>서울 중구 명동3길 5 위드빌딩 6층</span>
            </div>
            <div className="flex items-center justify-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center text-gold-shine flex-shrink-0"><i className="ri-time-line"></i></span>
              <span>매일 11:00 ~ 21:00</span>
            </div>
            <div className="flex items-center justify-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center text-gold-shine flex-shrink-0"><i className="ri-global-line"></i></span>
              <span>한 · EN · 中 · 日 대응</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Down Arrow */}
      <button
        onClick={() => scrollTo('#brand')}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 hover:text-gold-shine transition-colors duration-200 cursor-pointer animate-bounce"
      >
        <i className="ri-arrow-down-line text-2xl"></i>
      </button>
    </section>
  );
}
